// react
import { ReactNode, useContext, useMemo } from 'react';

// types
import { ActionButton } from '../../../../@types';

// router
import { useSearchParams } from 'react-router-dom';

// i18next
import { useTranslation } from 'react-i18next';

// context
import {
	ContextHistoricalTransactions,
	ContextProject,
} from '../../../pages/Project/Project.context';

// mui
import { Grid } from '@mui/material';

// icons
import { InsertDriveFileOutlined as IconInsertDriveFile } from '@mui/icons-material';

// ui
import { TableHeaderProps } from '../../../ui/TableHeaders/TableHeader/TableHeader';
import { TableHeaders } from '../../../ui/TableHeaders/TableHeaders';
import { TableFooter } from '../../../ui/TableFooter/TableFooter';

// cards
import { CardEmpty } from '../../../cards/CardEmpty/CardEmpty';
import { CardHistoricalTransaction } from '../../../cards/CardHistoricalTransaction/CardHistoricalTransaction';

// fields
import { SelectTransactionPage } from '../../../fields/SelectTransactionPage/SelectTransactionPage';

// forms
import { FormSearch } from '../../../forms/FormSearch/FormSearch';

// props
import { EmptyProps } from '../../../ui/Empty/Empty';

// props
export interface TableHistoricalTransactionsProps {
	hasSearch?: boolean;
}

export const TableHistoricalTransactions = ({
	hasSearch = true,
}: TableHistoricalTransactionsProps) => {
	// context
	const {
		historicalTransactions,
		isLoadingHistoricalTransactions,
		paginationHistoricalTransactions,
	} = useContext(ContextHistoricalTransactions);
	const { project } = useContext(ContextProject);

	// hooks
	const { t } = useTranslation();

	// state
	const [searchParams, setSearchParams] = useSearchParams();

	// params
	const paramQ = searchParams.get('q');
	const paramExpenseType = searchParams.get('expenseType');

	// vars
	const headers: TableHeaderProps[] = [
		{
			GridProps: {
				lg: 3,
			},
			isSortable: true,
			label: t('name'),
			value: 'name',
		},
		{
			GridProps: {
				lg: 2,
			},
			label: t('created'),
			value: 'created',
		},
		{
			GridProps: {
				lg: 2,
			},
			label: t('startDate'),
			value: 'dateStart',
		},
		{
			GridProps: {
				lg: 2,
			},
			label: t('endDate'),
			value: 'dateEnd',
		},
		{
			GridProps: {
				lg: 3,
			},
			label: t('uploaded'),
			value: 'user',
		},
	];

	// memos
	const empty = useMemo(() => {
		const emptyProps: EmptyProps = {
			actions: [],
			content: { children: t('empty.historicalTransactions.content') },
			IconCircleProps: { icon: (<IconInsertDriveFile />) as ReactNode },
			title: { children: t('empty.historicalTransactions.title') },
		};

		if (emptyProps.actions) {
			emptyProps.actions.push({
				children: t('uploadHistoricalTransactions'),
				onClick: () => {
					searchParams.set('dialog', 'uploadHistoricalTransactions');
					setSearchParams(searchParams);
				},
				variant: 'contained' as ActionButton['variant'],
			});
		}

		if (paramQ || paramExpenseType) {
			emptyProps.actions = [];
			emptyProps.content = { children: t('empty.default.content') };
			emptyProps.IconCircleProps = { icon: null };
		}

		return emptyProps;
	}, [paramQ, paramExpenseType, searchParams, setSearchParams, t]);

	const content = useMemo(() => {
		if (isLoadingHistoricalTransactions || !project || !historicalTransactions.length) {
			return <CardEmpty empty={empty} isLoading={isLoadingHistoricalTransactions} />;
		}

		return (
			<Grid container spacing={2}>
				{historicalTransactions.map((document, i) => {
					return (
						<Grid item key={`document-${i}`} xs={12}>
							<CardHistoricalTransaction document={document} />
						</Grid>
					);
				})}
			</Grid>
		);
	}, [empty, historicalTransactions, isLoadingHistoricalTransactions, project]);

	return (
		<Grid container spacing={2}>
			<Grid item xs={12}>
				<Grid container spacing={2}>
					<Grid item xs={12} md={3} lg={2}>
						<SelectTransactionPage value="historical" />
					</Grid>
					{hasSearch && (
						<Grid item xs={12} md={true}>
							<FormSearch InputProps={{ placeholder: t('searchByName') }} />
						</Grid>
					)}
				</Grid>
			</Grid>
			<Grid item xs={12} sx={{ display: { xs: 'none', lg: 'block' } }}>
				<TableHeaders headers={headers} />
			</Grid>
			<Grid item xs={12}>
				{content}
			</Grid>
			<Grid item xs={12}>
				<TableFooter
					numShowing={historicalTransactions.length}
					pagination={paginationHistoricalTransactions}
				/>
			</Grid>
		</Grid>
	);
};
